import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to manage audio playback in the game with binary volume control
 */
export const useAudio = () => {
  // Audio refs
  const inactiveGameMusicRef = useRef(null);
  const isPreGameGameMusicRef = useRef(null);
  
  // State
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [playPreGameMusic, setPlayPreGameMusic] = useState(false);
  const [isMusicOn, setIsMusicOn] = useState(() => {
    try {
      const storedValue = localStorage.getItem('isMusicOn');
      return storedValue !== null ? storedValue === 'true' : true;
    } catch (error) {
      console.error('Error loading isMusicOn from localStorage:', error);
      return true;
    }
  });
  
  // Add detection for problematic browsers
  const isRestrictedBrowserRef = useRef(false);
  
  // Check if running on iOS or other problematic platforms
  useEffect(() => {
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    // Add other browser checks as needed
    
    isRestrictedBrowserRef.current = isIOS;
    console.log("Detected restricted browser:", isRestrictedBrowserRef.current);
  }, []);
  
  // Store the maximum volume for each track
  const maxVolumes = useRef({
    inactive: 1.0,  // Original volume for inactive music
    preGame: 0.5    // Original volume for pre-game music
  });

  // Function to apply volume based on isMusicOn state
  const applyVolumeSettings = () => {
    if (!inactiveGameMusicRef.current || !isPreGameGameMusicRef.current) return;
    
    if (isRestrictedBrowserRef.current) {
      // For restricted browsers: use pause/play instead of volume
      if (!isMusicOn) {
        // Pause both tracks
        inactiveGameMusicRef.current.pause();
        isPreGameGameMusicRef.current.pause();
      } else {
        // Resume the appropriate track
        if (playPreGameMusic) {
          isPreGameGameMusicRef.current.play().catch(err => console.log('Play error:', err));
        } else {
          inactiveGameMusicRef.current.play().catch(err => console.log('Play error:', err));
        }
      }
    } else {
      // For normal browsers: use volume control
      if (isMusicOn) {
        // Full volume (each track's max volume)
        inactiveGameMusicRef.current.volume = maxVolumes.current.inactive;
        isPreGameGameMusicRef.current.volume = maxVolumes.current.preGame;
      } else {
        // Muted (volume 0)
        inactiveGameMusicRef.current.volume = 0;
        isPreGameGameMusicRef.current.volume = 0;
      }
    }
  };

  // Toggle music on/off
  const toggleMusic = () => {
    setIsMusicOn(prev => !prev);
  };

  // Initialize audio elements
  useEffect(() => {
    // Create audio elements if they don't exist
    if (!inactiveGameMusicRef.current) {
      inactiveGameMusicRef.current = new Audio('/sounds/music-rest.wav');
      inactiveGameMusicRef.current.loop = true;
      
      // Set initial volume (even for restricted browsers, as a fallback)
      inactiveGameMusicRef.current.volume = isMusicOn ? maxVolumes.current.inactive : 0;
    }
    
    // Initialize pre-game music
    if (!isPreGameGameMusicRef.current) {
      isPreGameGameMusicRef.current = new Audio('/sounds/music-playing.wav');
      isPreGameGameMusicRef.current.loop = true;
      
      // Set initial volume (even for restricted browsers, as a fallback)
      isPreGameGameMusicRef.current.volume = isMusicOn ? maxVolumes.current.preGame : 0;
    }
    
    // Cleanup function to stop audio when component unmounts
    return () => {
      if (inactiveGameMusicRef.current) {
        inactiveGameMusicRef.current.pause();
        inactiveGameMusicRef.current.currentTime = 0;
      }
      if (isPreGameGameMusicRef.current) {
        isPreGameGameMusicRef.current.pause();
        isPreGameGameMusicRef.current.currentTime = 0;
      }
    };
  }, []);

  // Apply volume settings whenever isMusicOn changes
  useEffect(() => {
    applyVolumeSettings();
    if (isMusicOn) {
      initializeOrPlay();
    }
  }, [isMusicOn]);

  // Control inactive music - modified for restricted browsers
  useEffect(() => {
    if (!inactiveGameMusicRef.current) return;
    
    // Function to handle playing just the inactive track
    const updateMusic = () => {
      const shouldPlayInactive = !playPreGameMusic;
      const inactiveIsPlaying = !inactiveGameMusicRef.current.paused;
      
      // Only handle the inactive music now
      if (shouldPlayInactive && !inactiveIsPlaying && isMusicOn) {
        // Always set volume in case browser supports it
        inactiveGameMusicRef.current.volume = maxVolumes.current.inactive;
        inactiveGameMusicRef.current.play().catch(err => console.log('Audio play error:', err));
      } else if (!shouldPlayInactive && inactiveIsPlaying) {
        inactiveGameMusicRef.current.pause();
      }
    };
    
    // Update music based on playPreGameMusic flag
    updateMusic();
  }, [playPreGameMusic, isMusicOn]);

  // Control pre-game music - modified for restricted browsers
  useEffect(() => {
    if (!isPreGameGameMusicRef.current) return;
    
    if (playPreGameMusic) {
      const isPlaying = !isPreGameGameMusicRef.current.paused;
      if (!isPlaying && isMusicOn) {
        // Always set volume in case browser supports it
        isPreGameGameMusicRef.current.volume = maxVolumes.current.preGame;
        isPreGameGameMusicRef.current.play().catch(err => console.log('Pre-game audio play error:', err));
      }
    } else {
      isPreGameGameMusicRef.current.pause();
      isPreGameGameMusicRef.current.currentTime = 0;
    }
  }, [playPreGameMusic, isMusicOn]);

  // Replace the initializeAudio function with initializeOrPlay
  const initializeOrPlay = () => {
    // If audio is already initialized, just make sure it's playing
    if (audioInitialized) {
      // Only play if music should be on
      if (isMusicOn) {
        if (playPreGameMusic) {
          // Resume pre-game music if it's paused
          if (isPreGameGameMusicRef.current && isPreGameGameMusicRef.current.paused) {
            isPreGameGameMusicRef.current.volume = maxVolumes.current.preGame;
            isPreGameGameMusicRef.current.play().catch(err => console.log('Resume pre-game audio error:', err));
          }
        } else {
          // Resume inactive music if it's paused
          if (inactiveGameMusicRef.current && inactiveGameMusicRef.current.paused) {
            inactiveGameMusicRef.current.volume = maxVolumes.current.inactive;
            inactiveGameMusicRef.current.play().catch(err => console.log('Resume inactive audio error:', err));
          }
        }
      }
      return; // Already initialized, just ensured correct track is playing
    }

    // Initialize audio for the first time
    // Create a silent audio context to unlock audio
    const context = new (window.AudioContext || window.webkitAudioContext)();
    
    // Initialize both audio tracks
    const promises = [];
    
    if (inactiveGameMusicRef.current) {
      promises.push(
        inactiveGameMusicRef.current.play()
          .then(() => {
            inactiveGameMusicRef.current.pause();
            inactiveGameMusicRef.current.currentTime = 0;
          })
          .catch(err => console.error("Failed to initialize inactive audio:", err))
      );
    }
    
    if (isPreGameGameMusicRef.current) {
      promises.push(
        isPreGameGameMusicRef.current.play()
          .then(() => {
            isPreGameGameMusicRef.current.pause();
            isPreGameGameMusicRef.current.currentTime = 0;
          })
          .catch(err => console.error("Failed to initialize pre-game audio:", err))
      );
    }
    
    // When all audio is initialized
    Promise.all(promises)
      .then(() => {
        // Only play music if it should be on
        if (isMusicOn) {
          // Play the appropriate music based on current state
          if (playPreGameMusic) {
            isPreGameGameMusicRef.current.volume = maxVolumes.current.preGame;
            isPreGameGameMusicRef.current.play().catch(err => console.log('Audio play error:', err));
          } else {
            inactiveGameMusicRef.current.volume = maxVolumes.current.inactive;
            inactiveGameMusicRef.current.play().catch(err => console.log('Audio play error:', err));
          }
        }
        
        setAudioInitialized(true);
      })
      .catch(err => console.error("Audio initialization failed:", err));
  };

  // Helper to start pre-game music
  const startPreGameMusic = () => {
    setPlayPreGameMusic(true);
    
    // Initialize and play pre-game music
    if (isPreGameGameMusicRef.current && isMusicOn) {
      isPreGameGameMusicRef.current.currentTime = 0;
      isPreGameGameMusicRef.current.volume = maxVolumes.current.preGame;
      isPreGameGameMusicRef.current.play().catch(err => console.log('Pre-game audio play error:', err));
    }
    
    // Ensure audio is initialized
    initializeOrPlay();
  };

  // Helper to stop pre-game music
  const stopPreGameMusic = () => {
    setPlayPreGameMusic(false);
  };

  // Add an effect to save isMusicOn to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('isMusicOn', isMusicOn.toString());
    } catch (error) {
      console.error('Error saving isMusicOn to localStorage:', error);
    }
  }, [isMusicOn]);

  // Return the public API for the hook
  return {
    initializeOrPlay,
    startPreGameMusic,
    stopPreGameMusic,
    audioInitialized,
    isMusicOn,
    toggleMusic
  };
}; 